import { useState, useEffect } from "react";
import { getDashboardSummaryCards } from "utils/supabase";
import CardStats from "components/Cards/CardStats.js";
import { ClipboardList, FileCheck2, Flame, Target } from "lucide-react";

const iconMap = {
  "clipboard-list": ClipboardList,
  "file-check": FileCheck2,
  target: Target,
  flame: Flame,
};

const colorMap = {
  indigo: "bg-indigo-100 text-indigo-500",
  sky: "bg-blue-100 text-blue-500",
  pink: "bg-pink-100 text-pink-500",
  orange: "bg-orange-100 text-orange-500",
};

function normalizeSummaryCards(rawData) {
  if (!rawData) return [];

  if (Array.isArray(rawData)) {
    if (rawData.length > 0 && rawData[0]?.cards) {
      return Array.isArray(rawData[0].cards) ? rawData[0].cards : [];
    }

    // Some RPCs can return cards directly as an array.
    if (rawData[0]?.title || rawData[0]?.key) {
      return rawData;
    }

    return [];
  }

  if (Array.isArray(rawData.cards)) {
    return rawData.cards;
  }

  return [];
}

function parseNumeric(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function isStreakCard(card) {
  const title = String(card?.title || "").toLowerCase();
  const key = String(card?.key || "").toLowerCase();
  const icon = String(card?.icon || "").toLowerCase();

  return title.includes("racha") || key.includes("racha") || key.includes("streak") || icon === "flame";
}

function isAverageCard(card) {
  const title = String(card?.title || "").toLowerCase();
  const key = String(card?.key || "").toLowerCase();

  return title.includes("promedio") || key.includes("promedio") || key.includes("average");
}

function getActivityType(card) {
  const text = `${card?.title || ""} ${card?.key || ""}`.toLowerCase();
  if (text.includes("pregunta")) return "preguntas";
  if (text.includes("examen") || text.includes("exámenes")) return "exámenes";
  return null;
}

function getActivityPhrase(card, changePercentage) {
  const activityType = getActivityType(card);
  if (!activityType) return "";

  const change = parseNumeric(changePercentage);
  if (change > 0) return `Vas avanzando con tus ${activityType}.`;
  if (change < 0) return `Sigue practicando tus ${activityType}.`;
  return `Mantén el ritmo con tus ${activityType}.`;
}

function getStreakPhrase(days) {
  if (days <= 0) return "Hoy es un gran dia para comenzar tu racha.";
  if (days === 1) return "Llevas 1 dia. Lo mas dificil ya empezo.";
  if (days <= 3) return "Excelente inicio. Mantente constante.";
  if (days <= 7) return "Vas muy bien. Ya construyes un habito.";
  if (days <= 14) return "Gran ritmo. Tu disciplina se nota.";
  if (days <= 30) return "Impresionante constancia. Sigue asi.";
  return "Racha de alto nivel. Eres ejemplo de consistencia.";
}

function getAveragePhrase(percent) {
  if (percent <= 0) return "Cada intento suma. Empieza hoy a subir tu promedio.";
  if (percent < 40) return "Buen inicio. Enfocate en constancia para mejorar.";
  if (percent < 60) return "Vas en progreso. Sigue practicando para despegar.";
  if (percent < 75) return "Buen rendimiento. Estas cerca del siguiente nivel.";
  if (percent < 90) return "Muy buen promedio. Mantienes un ritmo solido.";
  return "Excelente promedio. Trabajo sobresaliente.";
}

export default function HeaderStats() {
  const [statsCards, setStatsCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadStats() {
      try {
        const data = await getDashboardSummaryCards();

        if (mounted) {
          setStatsCards(normalizeSummaryCards(data));
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadStats();

    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="relative bg-indigo-900 pt-24 pb-24 md:pb-32" style={{ zIndex: 1 }}>
        <div className="px-4 md:px-10 mx-auto w-full">
          <div className="w-full">
            <div className="flex flex-wrap">
              {[0, 1, 2, 3].map((idx) => (
                <div key={idx} className="w-full sm:w-6/12 lg:w-3/12 px-2 mb-3 xl:mb-0">
                  <CardStats loading />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function getArrowDirection(changePercentage) {
    if (changePercentage === 0) return "same";
    return changePercentage > 0 ? "up" : "down";
  }

  function getPercentColor(changePercentage) {
    if (changePercentage === 0) return "text-gray-500";
    return changePercentage > 0 ? "text-emerald-500" : "text-red-500";
  }

  return (
    <>
      <div className="relative bg-indigo-900 pt-24 pb-24 md:pb-32" style={{ zIndex: 1 }}>
        <div className="px-4 md:px-10 mx-auto w-full">
          <div className="w-full">
            <div className="flex flex-wrap">
              {statsCards.map((card) => {
                const IconComponent = iconMap[card.icon];
                const iconColorClass = colorMap[card.color];
                const streakCard = isStreakCard(card);
                const averageCard = isAverageCard(card);
                const activityType = getActivityType(card);
                const customFooterCard = streakCard || averageCard || activityType;
                const streakDays = Math.max(0, Math.floor(parseNumeric(card.value)));
                const averagePercent = Math.max(0, Math.min(100, parseNumeric(card.value)));
                const statTitle = streakCard
                  ? `${streakDays} ${streakDays === 1 ? "dia" : "dias"}`
                  : `${card.value}${card.value_suffix}`;

                return (
                  <div key={card.key} className="w-full sm:w-6/12 lg:w-3/12 px-2 mb-3 xl:mb-0">
                    <CardStats
                      statSubtitle={card.title}
                      statTitle={statTitle}
                      statArrow={getArrowDirection(card.change_percentage)}
                      statPercent={Math.abs(card.change_percentage)}
                      statPercentColor={getPercentColor(card.change_percentage)}
                      statDescription={card.change_label}
                      hidePercent={customFooterCard}
                      statFooterMessage={
                        activityType
                          ? getActivityPhrase(card, card.change_percentage)
                          : streakCard
                          ? getStreakPhrase(streakDays)
                          : averageCard
                            ? getAveragePhrase(averagePercent)
                            : ""
                      }
                      statFooterColorClass="text-slate-500"
                      statIcon={IconComponent ? <IconComponent size={18} /> : null}
                      statIconColor={iconColorClass}
                    />
                  </div>
                );
              })}
              {!statsCards.length ? (
                <div className="w-full px-3">
                  <div className="text-sm text-blueGray-400">No hay estadísticas disponibles por el momento.</div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}